import Image from "next/image";
import { useNavigator } from "@/contexts/navigator/NavigatorContext";
import { Competency, LearnerOutcome } from "@/lib/models";
import { HtmlRenderer } from "../HtmlRenderer";
import { OutcomeAreaCompetencyRow } from "./OutcomeAreaCompetencyRow";
import TriangleDown from "../icons/TriangleDown";
import { LearnerOutcomeCompetenciesBackground } from "./LearnerOutcomeCompetenciesBackground";
import colors from "@/lib/static/colors";

type LearnerOutcomeDetailsProps = {
  learnerOutcome: LearnerOutcome;
  activeCompetency?: Competency;
};

export const LearnerOutcomeDetails = ({
  learnerOutcome,
}: LearnerOutcomeDetailsProps) => {
  const { activeCompetencyId } = useNavigator();

  const triangleFillColor = colors[`${learnerOutcome.id}-60`];
  const backgroundFillColor = colors[`${learnerOutcome.id}-40`];

  return (
    <div
      className={`${
        activeCompetencyId ? "hidden" : "grid"
      } relative z-10 w-full grid-cols-4 gap-4 small:grid-cols-8 medium:grid-cols-12 large:mt-6 large:gap-6`}
    >
      <div className="col-span-full mx-4 flex flex-col items-center small:col-span-6 small:col-start-2 medium:col-span-10 medium:col-start-2 large:col-span-8 large:col-start-3">
        {!!learnerOutcome.learnerOutcomeCard.sourceUrl && (
          <Image
            src={learnerOutcome.learnerOutcomeCard.sourceUrl}
            width={140}
            height={160}
            alt={learnerOutcome.name}
            className="mt-6 h-[152px] w-[132px] medium:mt-8 medium:h-[160px] medium:w-[140px]"
          />
        )}
        <h2 className="mt-6 text-center font-roobert text-xs font-normal uppercase text-neutral-800 medium:mt-8 medium:font-medium">
          Learner Outcome
        </h2>

        <div className="mt-6 text-center font-roobert text-[32px] font-medium leading-9 text-black medium:mt-8 medium:text-[40px]">
          {learnerOutcome.name}
        </div>
        <div className="mt-6 text-center font-roobert text-base font-normal leading-tight text-neutral-800 medium:mt-8 medium:text-xl">
          <HtmlRenderer html={learnerOutcome.description} />
        </div>

        <TriangleDown className="my-6" fill={triangleFillColor} />
      </div>

      <div className="relative col-span-10 col-start-2 hidden flex-col items-center overflow-hidden rounded-3xl border border-stone-200 bg-white medium:flex large:col-span-8 large:col-start-3">
        <LearnerOutcomeCompetenciesBackground
          fill={backgroundFillColor}
          className="absolute z-0 h-full w-full"
        />
        <div className="z-10 flex flex-col items-center">
          <h3 className="mb-8 mt-10 text-center font-roobert text-xs font-medium uppercase  text-black">
            Competencies
          </h3>
          {learnerOutcome.outcomeAreas.map((outcomeAreaId) => {
            return (
              <OutcomeAreaCompetencyRow
                key={outcomeAreaId}
                outcomeAreaId={outcomeAreaId}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
